import axios from 'axios';
import { useQuery } from 'react-query';
import { ContentIntelligenceFilters, ContentIntelligenceResponse } from 'pages/content/content-intelligence/types';
import { normalizeJSONAPIResponse } from 'utils/modelUtils';

const getContentIntelligenceItems = async (
  partnerId: string,
  pageNumber: number = 1,
  pageSize: number = 25,
  sortColumn: string = 'difficulty',
  sortDirection: 'asc' | 'desc' = 'desc',
  searchName: string = '',
  filters: ContentIntelligenceFilters = {}
): Promise<ContentIntelligenceResponse> => {
  const endpoint = `/api/v3/partners/${partnerId}/content_intelligence`;

  // Build sort param (prefix with - for descending)
  const sortParam = sortDirection === 'desc' ? `-${sortColumn}` : sortColumn;

  let queryParams = `page[number]=${pageNumber}&page[size]=${pageSize}&sort=${sortParam}`;

  if (searchName) {
    queryParams += `&filter[name]=${encodeURIComponent(searchName)}`;
  }

  // Add difficulty level filters
  if (filters.difficultyLevel && filters.difficultyLevel.length > 0) {
    filters.difficultyLevel.forEach((level) => {
      queryParams += `&filter[difficulty_level][]=${level}`;
    });
  }

  // Add template (item type) filters
  if (filters.template && filters.template.length > 0) {
    filters.template.forEach((template) => {
      queryParams += `&filter[template_type][]=${encodeURIComponent(template)}`;
    });
  }

  const response = await axios.get(`${endpoint}?${queryParams}`);
  const normalizedData = normalizeJSONAPIResponse(response.data);

  return {
    data: normalizedData,
    meta: response.data.meta,
  };
};

export const useContentIntelligenceItems = (
  partnerId: string,
  pageNumber: number = 1,
  pageSize: number = 25,
  sortColumn: string = 'difficulty',
  sortDirection: 'asc' | 'desc' = 'desc',
  searchName: string = '',
  filters: ContentIntelligenceFilters = {}
) => {
  return useQuery<ContentIntelligenceResponse>(
    ['contentIntelligenceItems', partnerId, pageNumber, pageSize, sortColumn, sortDirection, searchName, filters],
    () => getContentIntelligenceItems(partnerId, pageNumber, pageSize, sortColumn, sortDirection, searchName, filters),
    {
      enabled: !!partnerId,
      keepPreviousData: true,
    }
  );
};
